import React, { useEffect, useRef } from "react";
import { ResultCard, ResultListbox } from "./styled";

import { Key } from "Constants/Events";
import { Result } from "../interfaces";
import { useActiveDescendant } from "./hooks";

interface Props {
  id: string;
  listboxId: string;
  labelledBy: string;
  inputId: string;
  name?: string;
  options: Result[];
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  autoSelectResults?: boolean;
}

export const ResultSelect = ({
  id,
  listboxId,
  labelledBy,
  name,
  options,
  value,
  onChange,
  autoSelectResults,
}: Props): React.ReactElement => {
  const selectRef = useRef<HTMLSelectElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    autoSelectResults && optionsRef.current?.focus();
  }, [options]);

  const {
    activeDescendant,
    setInitialActiveDescendant,
    unsetActiveDescendant,
    setNextActiveDescendant,
    setPreviousActiveDescendant,
  } = useActiveDescendant(options);

  const handleResultSelect = (resultValue: string): void => {
    if (selectRef.current) {
      const changeEvent = new Event("change", {
        bubbles: true,
        cancelable: false,
      });
      selectRef.current.value = resultValue;
      selectRef.current.dispatchEvent(changeEvent);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent): void => {
    switch (event.key) {
      case Key.ARROW_RIGHT:
      case Key.ARROW_DOWN: {
        event.preventDefault();
        setNextActiveDescendant();
        break;
      }
      case Key.ARROW_LEFT:
      case Key.ARROW_UP: {
        event.preventDefault();
        setPreviousActiveDescendant();
        break;
      }
      case Key.SPACE:
      case Key.ENTER: {
        event.preventDefault();
        activeDescendant && handleResultSelect(activeDescendant);
        break;
      }
      default:
        break;
    }
  };

  return (
    <>
      <ResultListbox
        role="listbox"
        id={listboxId}
        aria-expanded={options.length > 0}
        aria-labelledby={labelledBy}
        aria-activedescendant={activeDescendant ? activeDescendant : ""}
        onFocus={setInitialActiveDescendant}
        onBlur={unsetActiveDescendant}
        onKeyDown={handleKeyPress}
        tabIndex={0}
        hidden={options.length === 0}
        ref={optionsRef}
      >
        {options.map(({ label, value: resultValue, selectable = false }) => {
          return (
            <ResultCard
              variant="border"
              forwardedAs="li"
              key={resultValue}
              id={resultValue}
              role="option"
              aria-selected={resultValue === value}
              onClick={() => handleResultSelect(resultValue)}
              className={
                resultValue === activeDescendant ? "active" : undefined
              }
              selectable={selectable}
            >
              {label}
            </ResultCard>
          );
        })}
      </ResultListbox>
      <select
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        ref={selectRef}
        hidden
      >
        {options.map(({ value: resultValue }) => (
          <option
            key={`${id}-${resultValue}`}
            value={resultValue}
            id={`${id}-${resultValue}`}
          ></option>
        ))}
      </select>
    </>
  );
};
